import React from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  analysis,
  editicongreen,
  humangrey,
  noteedit,
  sethescope,
  todayorder,
} from "./svgs";

import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { patientPhotoUrl } from "../../config/practice";

const FALLBACK_AVATAR =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect width="64" height="64" fill="#EEF1F6"/><circle cx="32" cy="25" r="11" fill="#C3CBDA"/><path d="M8 62c0-13 11-21 24-21s24 8 24 21z" fill="#C3CBDA"/></svg>'
  );

// "02:30 PM" out of the pre-composed "10-08-2025 02:30 PM" the history row carries.
const visitTimeOf = (row) => {
  const m = String(row?.start_date || "").match(/(\d{1,2}:\d{2}\s*[AP]M)/i);
  return m ? m[1].replace(/^0/, "") : "";
};

// The imported svgs hardcode their own fill/stroke, so text colour does not
// reach them; this flattens any of them to white.
const WHITE_GLYPH = "[&>svg]:brightness-0 [&>svg]:invert";

const statusPill = (status) => {
  const s = String(status || "").toUpperCase();
  if (s === "COMPLETED")
    return "bg-[#E8F4F2] text-[#1F6F63] border-[#CDE7E1]";
  if (s === "CANCELLEDTASK" || s === "CANCELLED")
    return "bg-[#FEF2F2] text-[#C10007] border-[#FFC9C9]";
  return "bg-[#F1F3F7] text-[#48526A] border-[#E3E8EF]";
};

const sentenceCase = (v) =>
  typeof v === "string" && v.length
    ? v.charAt(0).toUpperCase() + v.slice(1).toLowerCase()
    : v;

// One card per group: coloured top edge, icon tile, count, then the rows.
const OrderGroup = ({ icon, title, accent, tint, items, emptyText, renderRow }) => {
  const list = Array.isArray(items) ? items : [];
  return (
    <div className="overflow-hidden rounded-[14px] bg-white shadow-sm">
      <div className="h-[3px] w-full" style={{ background: accent }} />
      <div className="flex items-center gap-3 px-4 py-3">
        <span
          className={`flex h-9 w-9 flex-none items-center justify-center rounded-[10px] text-white [&>svg]:h-4 [&>svg]:w-4 ${WHITE_GLYPH}`}
          style={{ background: accent }}
        >
          {icon}
        </span>
        <span className="font-inter text-[15px] font-bold text-[#14213D]">
          {title}
        </span>
        <span
          className="ml-1 flex h-[22px] min-w-[22px] items-center justify-center rounded-full px-1.5 font-inter text-[12px] font-bold"
          style={{ background: tint, color: accent }}
        >
          {list.length}
        </span>
      </div>
      <div className="max-h-[220px] overflow-y-auto border-t border-[#EDF1F7]">
        {list.length > 0 ? (
          list.map((item, idx) => renderRow(item, idx))
        ) : (
          <div className="px-4 py-4 font-inter text-[13px] text-[#8A94A4]">
            {emptyText}
          </div>
        )}
      </div>
    </div>
  );
};

const OrderHistoryModal = ({
  isOpen,
  onClose,
  orderdetails,
  selectedAppointment,
  apphis,
  data,
}) => {
  const navigate = useNavigate();
  const start = selectedAppointment?.startdate;
  if (!isOpen) return null;

  const visitTime = visitTimeOf(selectedAppointment);
  const purpose = selectedAppointment?.purpose || data?.purpose;
  const confirmation = selectedAppointment?.visit_confirmation;
  // Level of visit for the clicked appointment. This used to be the literal 4.
  const lov = apphis?.lovtov?.[0]?.lov;

  const tile = (icon, label, value) => (
    <div className="min-w-0 rounded-[12px] bg-white/10 px-3 py-2.5">
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.8px] text-white/70">
        <span className={`[&>svg]:h-3.5 [&>svg]:w-3.5 ${WHITE_GLYPH}`}>
          {icon}
        </span>
        {label}
      </div>
      <div className="mt-1 truncate font-inter text-[14px] font-bold text-white">
        {value || "-"}
      </div>
    </div>
  );

  const row = (item, idx, accent) => (
    <div
      key={item?.id ?? idx}
      className="flex items-center justify-between gap-3 border-b border-[#F1F4F9] px-4 py-3 last:border-b-0"
    >
      <div className="flex min-w-0 items-stretch gap-3">
        <span
          className="w-[3px] flex-none rounded-full"
          style={{ background: accent }}
        />
        <div className="min-w-0">
          <div className="truncate font-inter text-[14px] font-bold text-[#14213D]">
            {item?.ordername || "-"}
          </div>
          <div className="truncate font-inter text-[13px] text-[#64748B]">
            {item?.ordertype}
          </div>
        </div>
      </div>
      {item?.status ? (
        <span
          className={`flex-none rounded-full border px-2.5 py-1 font-inter text-[12px] font-semibold ${statusPill(
            item.status
          )}`}
        >
          {sentenceCase(item.status)}
        </span>
      ) : null}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black bg-opacity-50 p-3 sm:p-6">
      <div className="relative my-auto w-full max-w-[560px] overflow-hidden rounded-[16px] bg-white text-left shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-[#EDF1F7] bg-white px-4 py-4 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <div className="min-w-0">
              <div className="font-inter text-[10px] font-bold uppercase tracking-[1px] text-[#9AA4B5]">
                Visit orders
              </div>
              <div className="truncate font-inter text-[17px] font-bold text-[#14213D] sm:text-[19px]">
                {start ? dayjs(start).format("dddd, MMM D, YYYY") : "-"}
              </div>
              <div className="truncate font-inter text-[13px] text-[#64748B]">
                {[visitTime, purpose].filter(Boolean).join(" · ")}
              </div>
            </div>
          </div>

          <div className="flex flex-none items-center gap-2">
            <button
              type="button"
              onClick={() =>
                navigate(`/orders/${data.appointmentid}/${data.startdate}/all`)
              }
              className="hidden items-center gap-1.5 rounded-[10px] border border-[#E3E8EF] px-3 py-2 font-inter text-[13px] font-semibold text-[#2c3357] hover:bg-[#F7F9FC] sm:flex"
            >
              {editicongreen} Edit order
            </button>
            <button
              type="button"
              onClick={() => onClose(false)}
              aria-label="Close"
              className="flex h-9 w-9 items-center justify-center rounded-[10px] border border-[#E3E8EF] text-[#64748B] hover:bg-[#F7F9FC]"
            >
              <CloseIcon style={{ width: 18, height: 18 }} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="space-y-4 bg-[#EEF1F8] px-3 py-4 sm:px-5 sm:py-5">
          {/* Edit order drops under the header on a phone rather than crowding it */}
          <button
            type="button"
            onClick={() =>
              navigate(`/orders/${data.appointmentid}/${data.startdate}/all`)
            }
            className="flex w-full items-center justify-center gap-1.5 rounded-[10px] border border-[#E3E8EF] bg-white px-3 py-2 font-inter text-[13px] font-semibold text-[#2c3357] sm:hidden"
          >
            {editicongreen} Edit order
          </button>

          {/* Patient */}
          <div className="rounded-[16px] bg-[#1d2951] p-4 sm:p-5">
            <div className="flex items-start gap-3 sm:gap-4">
              <div className="h-16 w-16 flex-none overflow-hidden rounded-full border-4 border-white/90 bg-gray-300 shadow-md">
                <img
                  src={patientPhotoUrl(data?.patientid)}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = FALLBACK_AVATAR;
                  }}
                  alt="Profile"
                  className="block h-full w-full object-cover"
                />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <h3 className="m-0 font-inter text-[18px] font-bold text-white sm:text-[20px]">
                    {`${data?.firstname || ""} ${data?.lastname || ""}`.trim() ||
                      "-"}
                  </h3>
                  <span className="font-inter text-[14px] font-semibold text-white/60">
                    #{data?.patientid || "-"}
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {purpose ? (
                    <span className="rounded-full border border-[#E9D4FF] bg-[#F3E8FF] px-2.5 py-1 font-inter text-[12px] font-semibold text-[#8200DB]">
                      {purpose}
                    </span>
                  ) : null}
                  {confirmation ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-[#CDE7E1] bg-[#E8F4F2] px-2.5 py-1 font-inter text-[12px] font-semibold text-[#1F6F63]">
                      <span className="h-[6px] w-[6px] rounded-full bg-current" />
                      {sentenceCase(confirmation)}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-2.5 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
              {tile(humangrey, "Insurance", data?.insurancename)}
              {tile(sethescope, "Analysis", data?.linked_provider)}
              <div className="rounded-[12px] bg-white/10 px-3 py-2.5">
                <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.8px] text-white/70">
                  <span className={`[&>svg]:h-3.5 [&>svg]:w-3.5 ${WHITE_GLYPH}`}>
                    {analysis}
                  </span>
                  Level
                </div>
                <div className="mt-1 flex h-[26px] min-w-[26px] items-center justify-center rounded-[8px] bg-white px-2 font-inter text-[14px] font-bold text-[#14213D]">
                  {lov === 0 || lov ? lov : "-"}
                </div>
              </div>
            </div>
          </div>

          <OrderGroup
            icon={todayorder}
            title="Today's orders"
            accent="#1d2951"
            tint="#E4E9F6"
            items={apphis?.notes}
            emptyText="No orders available"
            renderRow={(item, idx) => row(item, idx, "#8B5CF6")}
          />

          <OrderGroup
            icon={todayorder}
            title="Scheduled orders"
            accent="#50869A"
            tint="#E1EDF1"
            items={apphis?.notessch}
            emptyText="No scheduled orders"
            renderRow={(item, idx) => row(item, idx, "#50869A")}
          />

          <OrderGroup
            icon={noteedit}
            title="Notes"
            accent="#C08A2E"
            tint="#F7ECD9"
            items={orderdetails?.notes}
            emptyText="No notes available"
            renderRow={(note, index) => (
              <div
                key={index}
                className="flex items-stretch gap-3 border-b border-[#F1F4F9] px-4 py-3 last:border-b-0"
              >
                <span className="w-[3px] flex-none rounded-full bg-[#C08A2E]" />
                <div className="font-inter text-[13.5px] text-[#2c3357]">
                  {note?.ordername || "-"}
                </div>
              </div>
            )}
          />
        </div>
      </div>
    </div>
  );
};

export default OrderHistoryModal;
